// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { notifications } from '@mantine/notifications';
import { useMedplum } from '@medplum/react';
import { useCallback, useState } from 'react';
import { botIdentifier } from '../../../lib/bot-identifier';
import { showErrorNotification } from '../../../utils/notifications';

export interface UseRevokeSwePatients {
  revoke: (patientIds?: string[]) => Promise<void>;
  loading: boolean;
  error: string | undefined;
}

/**
 * Wraps `revoke-swe-patient-access` bot execution.
 *
 * Call with `patientIds` for selective revoke; omit to revoke all.
 * On success: shows a success toast and calls `onSuccess()` so the caller can
 * refresh / reload the patient list.
 */
export function useRevokeSwePatients(sweId: string, onSuccess: () => void): UseRevokeSwePatients {
  const medplum = useMedplum();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const revoke = useCallback(
    async (patientIds?: string[]): Promise<void> => {
      if (patientIds !== undefined && patientIds.length === 0) {
        return;
      }
      setLoading(true);
      setError(undefined);
      try {
        const payload: { sweId: string; patientIds?: string[] } = { sweId };
        if (patientIds !== undefined) {
          payload.patientIds = patientIds;
        }
        await medplum.executeBot(botIdentifier('revoke-swe-patient-access'), payload);
        const message =
          patientIds === undefined
            ? 'Successfully revoked access for all patients.'
            : `Successfully revoked access for ${patientIds.length} patient${patientIds.length === 1 ? '' : 's'}.`;
        notifications.show({
          color: 'green',
          title: 'Access revoked',
          message,
        });
        onSuccess();
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        setError(message);
        showErrorNotification(err);
      } finally {
        setLoading(false);
      }
    },
    [medplum, sweId, onSuccess]
  );

  return { revoke, loading, error };
}
